import type { ImportRecord, ImportState } from '@/services/db/import/entity';
import { generatePath } from '@/services/router/generate-path';

/**
 * The screens an import can be on.
 *
 * `detail` is the finished import's own page, outside the wizard; the rest are its steps.
 */
export type ImportWizardStep = 'detail' | 'upload' | ImportWaitingStep | 'fields' | 'preview' | 'review';

/**
 * The steps with nothing for the reader to do: the import is with the server, and the step
 * exists to say so until it moves.
 */
export type ImportWaitingStep = 'preprocessing' | 'validating' | 'importing';

/**
 * The steps this app serves. Each gains its entry here when its screen is ported, and the twin
 * URLs stop being needed once every step has (FPS-2702).
 */
export type MigratedWizardStep = ImportWaitingStep | 'preview' | 'fields' | 'review' | 'upload';

/**
 * The step each state has a screen of its own for, and `null` for the three that do not.
 *
 * The server decides where an import is; this says which screen shows it. Both the states the
 * server is working through and the ones it has stopped on are here, so a reader who returns to
 * an import days later lands on whatever it is now rather than where they left it.
 *
 * Every state is named, so one the spec gains is a compile error here rather than an import that
 * quietly falls through to the upload step. The three `null`s are the states with no single
 * answer: `preprocessed` has three steps the reader may be on, and neither `start` nor `canceled`
 * has a screen of its own.
 */
const STEP_FOR_STATE = {
  imported: 'detail',
  // A failed import goes back to the upload step, which is where a second file can be given.
  failed: 'upload',
  preprocessing: 'preprocessing',
  validating: 'validating',
  importing: 'importing',
  validated: 'review',
  preprocessed: null,
  start: null,
  canceled: null,
} satisfies Record<ImportState, ImportWizardStep | null>;

/**
 * Once the columns have been read, the reader may move between the step that works on them and
 * the review that follows — so being on either is not a reason to send them anywhere.
 *
 * Which step works on the columns depends on the type, and the other one is not a step this
 * import has: an event import previews its rows and never maps them, so a reader on `fields` is
 * on a screen with nothing to show them, and the reverse holds for every other type.
 */
function preprocessedSteps(columnsStep: ImportWizardStep): readonly ImportWizardStep[] {
  return [columnsStep, 'review'];
}

/**
 * The states whose steps offer a way back to the file. Both accept a reset
 * (`isResettableImportState`), so the reader can give the import a different one from here.
 */
const BACK_TO_UPLOAD_STATES: readonly ImportState[] = ['preprocessed', 'validated'];

type ImportWizardRedirectInput = {
  state: ImportState;
  /** An event import maps nothing: it previews its rows instead. */
  type: ImportRecord['type'];
  /** Where the reader is now. */
  currentStep: ImportWizardStep;
};

/**
 * Which step the reader belongs on, or null when they are already somewhere they may be.
 *
 * The wizard's steps are not a sequence the reader walks: the server moves the import along on
 * its own, and a URL can be opened at any time, so every screen has to answer whether it is
 * still the right one. That answer is this function, and it is the only place the question is
 * asked.
 */
export function importWizardRedirect({ state, type, currentStep }: ImportWizardRedirectInput): ImportWizardStep | null {
  // The wizard's own way back to the file — "Back" on the columns steps, "Back to upload" on the
  // review — points here from these two states, and the server accepts a reset from both. Asking
  // `STEP_FOR_STATE` first sent those readers straight back to the step they clicked from, so
  // every one of those links looped.
  //
  // Only these two, not every resettable state: a reader who opens the wizard while the server is
  // still preprocessing or validating belongs on the screen watching that, which is what the rest
  // of this function is for.
  if (currentStep === 'upload' && BACK_TO_UPLOAD_STATES.includes(state)) {
    return null;
  }

  const step = STEP_FOR_STATE[state];
  if (step != null) {
    return step === currentStep ? null : step;
  }

  if (state === 'preprocessed') {
    const columnsStep = type === 'event' ? 'preview' : 'fields';
    return preprocessedSteps(columnsStep).includes(currentStep) ? null : columnsStep;
  }

  // `start` has nothing uploaded yet and `canceled` never got past the columns, so both belong
  // on the upload step — the same fallthrough the embedded app takes for a state it has no
  // screen for.
  return currentStep === 'upload' ? null : 'upload';
}

/**
 * Where a step lives.
 *
 * Each step's own address: a migrated step is addressed at `/imports`, and one the embedded app
 * still serves at `/journeys/imports`. Both spellings resolve in either flag state — each
 * migrated screen is registered alongside a twin, and each twin forwards — so a step's entry
 * moves here when its screen is ported, and nothing else has to change.
 */
export function importStepHref(
  workspaceId: number,
  importId: string,
  step: ImportWizardStep,
  search?: Record<string, string | undefined>,
): string {
  const params = { workspaceId, importId };

  switch (step) {
    case 'preprocessing':
      return generatePath('/workspaces/:workspaceId/imports/:importId/setup/preprocessing', params, search);
    case 'validating':
      return generatePath('/workspaces/:workspaceId/imports/:importId/setup/validating', params, search);
    case 'importing':
      return generatePath('/workspaces/:workspaceId/imports/:importId/setup/importing', params, search);
    case 'preview':
      return generatePath('/workspaces/:workspaceId/imports/:importId/setup/preview', params, search);
    case 'fields':
      return generatePath('/workspaces/:workspaceId/imports/:importId/setup/fields', params, search);
    case 'review':
      return generatePath('/workspaces/:workspaceId/imports/:importId/setup/review', params, search);
    case 'upload':
      return generatePath('/workspaces/:workspaceId/imports/:importId/setup', params, search);
    case 'detail':
      return generatePath('/workspaces/:workspaceId/journeys/imports/:importId', params, search);
    default: {
      const _exhaustive: never = step;
      return String(_exhaustive);
    }
  }
}

/**
 * A waiting step's address in the embedded app.
 *
 * Only the migrated steps need this: it is the twin a migrated screen hands back to while the
 * flag is off, which is the one thing {@link importStepHref} cannot answer, since it returns
 * whichever address is canonical.
 */
export function importStepLegacyHref(workspaceId: number, importId: string, step: MigratedWizardStep): string {
  const params = { workspaceId, importId };

  switch (step) {
    case 'preprocessing':
      return generatePath('/workspaces/:workspaceId/journeys/imports/:importId/setup/preprocessing', params);
    case 'validating':
      return generatePath('/workspaces/:workspaceId/journeys/imports/:importId/setup/validating', params);
    case 'importing':
      return generatePath('/workspaces/:workspaceId/journeys/imports/:importId/setup/importing', params);
    case 'preview':
      return generatePath('/workspaces/:workspaceId/journeys/imports/:importId/setup/preview', params);
    case 'fields':
      return generatePath('/workspaces/:workspaceId/journeys/imports/:importId/setup/fields', params);
    case 'review':
      return generatePath('/workspaces/:workspaceId/journeys/imports/:importId/setup/review', params);
    case 'upload':
      return generatePath('/workspaces/:workspaceId/journeys/imports/:importId/setup', params);
    default: {
      const _exhaustive: never = step;
      return String(_exhaustive);
    }
  }
}

/**
 * The label the cancel event reports for a step, or undefined for a step it has no label for.
 *
 * These are the embedded app's own labels (`CANCELED_SOURCE_LABELS` in
 * `app/utils/analytics/transforms/import.ts`), which cover the three steps the reader works in
 * and nothing else. Reporting a step name of our own would split the same event into two
 * vocabularies, so a waiting step reports no source at all — as it does there.
 */
export function importStepAnalyticsSource(step: ImportWizardStep): string | undefined {
  switch (step) {
    case 'upload':
      return 'Upload CSV Tab';
    case 'fields':
      return 'Map Fields Tab';
    case 'review':
      return 'Review Tab';
    case 'detail':
    case 'preprocessing':
    case 'validating':
    case 'importing':
    case 'preview':
      return undefined;
    default: {
      const _exhaustive: never = step;
      return undefined;
    }
  }
}
